/* eslint-disable max-len */
// src/components/contentHub/InspirationSelector.js

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FiRefreshCw, FiCheckCircle, FiFeather, FiEdit3, FiLoader, FiArrowLeft, FiLayout, FiLayers, FiVideo, FiCamera, FiFilm, FiTrendingUp } from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';
import { FORMAT_PLACEHOLDERS, FORMAT_GUIDANCE } from '../shared/constants/workflowCopy';
import { getFormatConfig } from '../shared/constants/templateConstants';
import TrendsIntelligence from './TrendsIntelligence';

// --- Constants ---
const MIN_CUSTOM_IDEA_LENGTH = 20;

/** Extracts pillar name from either a plain string or structured {name, description, exampleTopic} object */
const getPillarName = (p) => (typeof p === 'object' && p !== null && p.name) ? p.name : String(p);

const FORMAT_ICON_MAP = {
    FiLayout: <FiLayout className="h-3.5 w-3.5" />,
    FiLayers: <FiLayers className="h-3.5 w-3.5" />,
    FiVideo: <FiVideo className="h-3.5 w-3.5" />,
    FiCamera: <FiCamera className="h-3.5 w-3.5" />,
    FiFilm: <FiFilm className="h-3.5 w-3.5" />,
};

/**
 * Step: The Spark (Inspiration Selector)
 * Allows the user to choose between Pre-defined Pillars, Trending Now, or a Custom Spark.
 */
function InspirationSelector({
    project,
    onGenerateRefinedDescription,
    onRefreshPillars,
    availablePillars,
    loading,
    onBack,
    workflowConfig,
    selectedTrend
}) {
    const { addToast } = useToast();
    const isExpressLane = workflowConfig?.workflow === 'single';

    // Format-aware derived values
    const format = workflowConfig?.format || 'standard';
    const formatConfig = getFormatConfig(format);
    const formatPlaceholder = FORMAT_PLACEHOLDERS[format] || FORMAT_PLACEHOLDERS.standard;
    const formatGuidance = FORMAT_GUIDANCE[format] || FORMAT_GUIDANCE.standard;
    const platforms = workflowConfig?.platforms || [];
    const platformLabel = platforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ');

    // --- State ---
    const [inspirationSource, setInspirationSource] = useState(
        selectedTrend ? 'custom' : (isExpressLane ? 'custom' : 'pillar')
    );
    const [selectedPillar, setSelectedPillar] = useState('');
    const [customIdea, setCustomIdea] = useState(
        selectedTrend ? `${selectedTrend.title}: ${selectedTrend.angle}` : ''
    );
    const [isRefreshingPillars, setIsRefreshingPillars] = useState(false);

    // --- Effects ---
    useEffect(() => {
        if (isExpressLane) {
            if (inspirationSource !== 'custom' && inspirationSource !== 'trends') setInspirationSource('custom');
        } else {
            if (availablePillars && availablePillars.length > 0 && !selectedPillar && inspirationSource === 'pillar') {
                setSelectedPillar(getPillarName(availablePillars[0]));
            } else if ((!availablePillars || availablePillars.length === 0) && inspirationSource === 'pillar') {
                setInspirationSource('custom');
            }
        }
    }, [availablePillars, selectedPillar, inspirationSource, isExpressLane]);


    // ... validation ...
    const isPillarSelected = inspirationSource === 'pillar' && selectedPillar.trim().length > 0;
    const isCustomIdeaValid = inspirationSource === 'custom' && customIdea.trim().length >= MIN_CUSTOM_IDEA_LENGTH;
    const canConfirmInspiration = (isPillarSelected || isCustomIdeaValid) && !loading && !isRefreshingPillars;

    // --- Handlers ---
    const handleSourceChange = useCallback((source) => {
        setInspirationSource(source);
        if (source === 'pillar') {
            setCustomIdea('');
            if (availablePillars && availablePillars.length > 0) {
                 setSelectedPillar(getPillarName(availablePillars[0]));
            } else {
                 setSelectedPillar('');
            }
        } else {
            setSelectedPillar('');
        }
    }, [availablePillars]);

    const handlePillarSelect = useCallback((pillar) => {
        setSelectedPillar(getPillarName(pillar));
    }, []);

    const handleCustomIdeaChange = useCallback((e) => {
        setCustomIdea(e.target.value);
    }, []);

   const handleConfirmInspiration = () => {
        if (!canConfirmInspiration) return;

        let topic = inspirationSource === 'pillar' ? selectedPillar : customIdea.trim();
        const type = inspirationSource === 'pillar' ? 'pillar' : 'custom';

        if (!topic || topic.trim().length < 3) {
            addToast("The selected inspiration is invalid. Please try again.", "warning");
            return;
        }

        onGenerateRefinedDescription(topic, type);
    };

    const handleSelectTrend = useCallback((trendText) => {
        setCustomIdea(trendText);
        setInspirationSource('custom');
        addToast('Trend idea loaded — review and submit when ready.', 'info');
    }, [addToast]);

    const handleRefreshPillarsClick = useCallback(async () => {
         if (loading || isRefreshingPillars) return;
         setIsRefreshingPillars(true);
         await onRefreshPillars(availablePillars);
         setIsRefreshingPillars(false);
     }, [loading, isRefreshingPillars, onRefreshPillars, availablePillars]);

    // --- Render ---
    return (
        <div className="bg-warm-gray/20 rounded-lg shadow-md border border-white/10 relative overflow-hidden">

            {/* --- Header Section --- */}
            <div className="p-6 border-b border-white/10 bg-warm-gray/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                    <button
                        onClick={onBack}
                        className="mr-4 p-2 text-cream/40 hover:text-cream rounded-full hover:bg-white/5 transition-colors"
                        title="Back to Mission Briefing"
                    >
                        <FiArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h2 className="text-lg font-semibold text-cream">The Spark</h2>
                        <p className="text-sm text-cream/60">{formatGuidance || 'What do you want to post about today?'}</p>
                    </div>
                </div>

                {/* Format + Platform Context Badge */}
                {formatConfig && (
                    <div className="flex items-center px-3 py-1.5 bg-slate-blue/15 border border-slate-blue/25 rounded-full">
                        <span className="text-slate-blue mr-1.5">{FORMAT_ICON_MAP[formatConfig.iconName]}</span>
                        <span className="text-xs font-bold text-cream/70">
                            {formatConfig.label}
                            {platformLabel && <span className="text-cream/40 font-medium"> · {platformLabel}</span>}
                        </span>
                    </div>
                )}
            </div>

            <div className="p-6 space-y-6">

                {/* Source Tabs — Single Post shows Trending + Custom; Series shows all 3 */}
                <div className="flex p-1 bg-warm-gray/30 rounded-2xl w-full md:w-fit border border-white/10">
                    {!isExpressLane && (
                        <button
                            onClick={() => handleSourceChange('pillar')}
                            disabled={loading}
                            className={`flex-1 flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                inspirationSource === 'pillar'
                                ? 'bg-slate-blue/20 text-slate-blue shadow-sm'
                                : 'text-cream/60 hover:text-cream'
                            }`}
                        >
                            <FiFeather className="mr-2"/> Pillars
                        </button>
                    )}
                    <button
                        onClick={() => handleSourceChange('trends')}
                        disabled={loading}
                        className={`flex-1 flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                            inspirationSource === 'trends'
                            ? 'bg-slate-blue/20 text-slate-blue shadow-sm'
                            : 'text-cream/60 hover:text-cream'
                        }`}
                    >
                        <FiTrendingUp className="mr-2"/> Trending
                    </button>
                    <button
                        onClick={() => handleSourceChange('custom')}
                        disabled={loading}
                        className={`flex-1 flex items-center justify-center px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                            inspirationSource === 'custom'
                            ? 'bg-slate-blue/20 text-slate-blue shadow-sm'
                            : 'text-cream/60 hover:text-cream'
                        }`}
                    >
                        <FiEdit3 className="mr-2"/> Custom
                    </button>
                </div>

                {/* Content Area */}
                <div className="min-h-[200px]">
                    {inspirationSource === 'pillar' && (
                        <div className="space-y-4 animate-fade-in">
                            <div className="flex items-center justify-between">
                                <label className="form-label">Choose a strategic topic:</label>
                                <button
                                    onClick={handleRefreshPillarsClick}
                                    disabled={loading || isRefreshingPillars}
                                    className="text-xs text-slate-blue hover:text-slate-blue/80 flex items-center hover:underline disabled:opacity-50 font-bold"
                                >
                                    <FiRefreshCw className={`mr-1 h-3 w-3 ${isRefreshingPillars ? 'animate-spin' : ''}`} /> Refresh Ideas
                                </button>
                            </div>

                            {availablePillars && availablePillars.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {availablePillars.map((pillar, index) => {
                                        const name = getPillarName(pillar);
                                        const isStructured = typeof pillar === 'object' && pillar.name;
                                        const isSelected = selectedPillar === name;
                                        return (
                                            <div
                                                key={index}
                                                onClick={() => !loading && handlePillarSelect(pillar)}
                                                className={`p-4 border rounded-2xl cursor-pointer transition-all duration-200 ${
                                                    isSelected
                                                    ? 'border-slate-blue bg-slate-blue/10 ring-2 ring-slate-blue/50 shadow-glow'
                                                    : 'border-white/10 hover:border-slate-blue/30 hover:bg-warm-gray/20'
                                                }`}
                                            >
                                                <div className="flex items-start">
                                                    <input
                                                        type="radio"
                                                        name="pillarSelection"
                                                        checked={isSelected}
                                                        readOnly
                                                        className="mt-1 h-4 w-4 text-slate-blue border-gray-300 focus:ring-indigo-500"
                                                    />
                                                    <div className="ml-3">
                                                        <span className="text-sm text-cream font-bold">{name}</span>
                                                        {isStructured && pillar.description && (
                                                            <p className="text-xs text-cream/50 mt-0.5 leading-relaxed">{pillar.description}</p>
                                                        )}
                                                        {isStructured && pillar.exampleTopic && (
                                                            <p className="text-xs text-slate-blue/60 italic mt-0.5">e.g. {pillar.exampleTopic}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                    <p className="text-sm text-gray-500 italic">No suggested pillars available.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {inspirationSource === 'trends' && (
                        <TrendsIntelligence
                            projectId={project?.id || ''}
                            onSelectTrend={handleSelectTrend}
                            disabled={loading}
                            platform={platforms?.[0] || 'meta'}
                        />
                    )}

                    {inspirationSource === 'custom' && (
                        <div className="space-y-3 animate-fade-in">
                            <label htmlFor="customIdea" className="form-label">Describe your idea:</label>
                            <textarea
                                id="customIdea"
                                rows="6"
                                value={customIdea}
                                onChange={handleCustomIdeaChange}
                                disabled={loading}
                                className="form-textarea"
                                placeholder={`${formatPlaceholder} (Min ${MIN_CUSTOM_IDEA_LENGTH} chars)`}
                            />
                            <div className="flex justify-end">
                                <span className={`text-xs font-bold ${customIdea.length > 0 && customIdea.length < MIN_CUSTOM_IDEA_LENGTH ? 'text-soft-coral' : 'text-cream/40'}`}>
                                    {customIdea.length} / {MIN_CUSTOM_IDEA_LENGTH} chars
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Action — hidden when on trends tab (user must select a trend first) */}
                {inspirationSource !== 'trends' && (
                    <div className="pt-6 border-t border-white/10 flex justify-end">
                        <button
                            onClick={handleConfirmInspiration}
                            disabled={!canConfirmInspiration}
                            className="inline-flex items-center justify-center px-6 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-95"
                        >
                            {loading ? <FiLoader className="mr-2 h-5 w-5 animate-spin" /> : <FiCheckCircle className="mr-2 h-5 w-5" />}
                            {loading ? 'Analyzing Idea...' : 'Next: Refine Brief'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

InspirationSelector.propTypes = {
    project: PropTypes.object.isRequired,
    onGenerateRefinedDescription: PropTypes.func.isRequired,
    onRefreshPillars: PropTypes.func.isRequired,
    availablePillars: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({name: PropTypes.string.isRequired, description: PropTypes.string, exampleTopic: PropTypes.string})])).isRequired,
    loading: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    selectedTrend: PropTypes.shape({
        title: PropTypes.string,
        angle: PropTypes.string,
        category: PropTypes.string,
        platform: PropTypes.string,
    }),
};

export default InspirationSelector;
